import type { PalletGroup, PalletGroupUnit } from '../api/palletsApi';

/**
 * Pallet configuration as the client edits it — the legacy pallet widget's model:
 * each pallet holds a number of inventory units of each line, so one line can be
 * split across pallets, and whatever no pallet holds stays loose.
 */
export interface PalletConfigUnit {
  uuid: string;
  typeId: number;
  /** Inventory units of each line on this pallet, keyed by line id; never 0 */
  items: Record<string, number>;
}

/** Pallets in their sequence order */
export type PalletConfig = PalletConfigUnit[];

/** Where some of a line's units sit: on a pallet, or loose (`palletId: null`) */
export interface PalletPortion {
  palletId: string | null;
  lineId: string;
}

/** Inventory units of each line, keyed by line id */
export type LineTotals = Record<string, number>;

export function fromPalletGroup(group: PalletGroup | undefined): PalletConfig {
  return Object.entries(group ?? {})
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, unit]) => ({
      uuid: unit.uuid,
      typeId: Number(unit.type),
      items: Object.fromEntries(
        Object.entries(unit.items ?? {}).flatMap(([lineId, item]) => (item.assigned ? [[lineId, item.assigned]] : []))
      )
    }));
}

export function toPalletGroup(config: PalletConfig): Record<string, PalletGroupUnit> {
  return Object.fromEntries(
    config.map((unit, i) => [
      String(i + 1),
      {
        type: String(unit.typeId),
        uuid: unit.uuid,
        items: Object.fromEntries(Object.entries(unit.items).map(([lineId, assigned]) => [lineId, { assigned }]))
      }
    ])
  );
}

const withUnits = (items: Record<string, number>, lineId: string, units: number) => {
  const { [lineId]: _, ...rest } = items;
  return units > 0 ? { ...rest, [lineId]: units } : rest;
};

const assignedUnits = (config: PalletConfig, lineId: string, exceptPalletId?: string) =>
  config.reduce((sum, unit) => (unit.uuid === exceptPalletId ? sum : sum + (unit.items[lineId] ?? 0)), 0);

/** Units of the line that sit on no pallet */
export const looseUnits = (config: PalletConfig, lineId: string, total: number) =>
  Math.max(total - assignedUnits(config, lineId), 0);

/** Most units of the line the pallet can hold: what the other pallets leave of the total */
export const maxUnitsOnPallet = (config: PalletConfig, palletId: string, lineId: string, total: number) =>
  Math.max(total - assignedUnits(config, lineId, palletId), 0);

export function portionUnits(config: PalletConfig, portion: PalletPortion, total: number): number {
  if (portion.palletId == null) return looseUnits(config, portion.lineId, total);
  return config.find((unit) => unit.uuid === portion.palletId)?.items[portion.lineId] ?? 0;
}

/** Adds empty pallets with client-generated UUIDs, which stay stable across saves */
export function addPallets(config: PalletConfig, typeId: number, count: number) {
  const added = Array.from({ length: count }, () => ({ uuid: crypto.randomUUID(), typeId, items: {} }));
  return { config: [...config, ...added], uuids: added.map((unit) => unit.uuid) };
}

/** Deletes the pallet; its units become loose */
export const removePallet = (config: PalletConfig, palletId: string) => config.filter((unit) => unit.uuid !== palletId);

/** Sets the units of the line on the pallet, capped by {@link maxUnitsOnPallet}; 0 takes the line off it */
export function setUnitsOnPallet(
  config: PalletConfig,
  palletId: string,
  lineId: string,
  units: number,
  total: number
): PalletConfig {
  const value = Math.min(Math.max(Math.floor(units), 0), maxUnitsOnPallet(config, palletId, lineId, total));
  return config.map((unit) =>
    unit.uuid === palletId ? { ...unit, items: withUnits(unit.items, lineId, value) } : unit
  );
}

/** Takes the portions off their pallets; their units become loose. Loose portions are left as they are. */
export const unassignPortions = (config: PalletConfig, portions: PalletPortion[]): PalletConfig =>
  config.map((unit) => ({
    ...unit,
    items: portions
      .filter((portion) => portion.palletId === unit.uuid)
      .reduce((items, portion) => withUnits(items, portion.lineId, 0), unit.items)
  }));

/**
 * Moves the portions onto the pallet: the loose units of a line join it, a
 * portion on another pallet leaves that pallet. Units already on the target add up.
 */
export function movePortions(
  config: PalletConfig,
  portions: PalletPortion[],
  palletId: string,
  totals: LineTotals
): PalletConfig {
  return portions.reduce((next, portion) => {
    if (portion.palletId === palletId) return next;
    const units = portionUnits(next, portion, totals[portion.lineId] ?? 0);
    if (units <= 0) return next;

    return next.map((unit) => {
      if (unit.uuid === portion.palletId) return { ...unit, items: withUnits(unit.items, portion.lineId, 0) };
      if (unit.uuid === palletId) {
        return { ...unit, items: withUnits(unit.items, portion.lineId, (unit.items[portion.lineId] ?? 0) + units) };
      }
      return unit;
    });
  }, config);
}

/**
 * Puts the portions on `count` new pallets, filling each up to `unitsPerPallet`
 * before starting the next one, so a portion that does not fit is split. Units
 * that do not fit on the new pallets stay loose.
 */
export function palletizePortions(
  config: PalletConfig,
  portions: PalletPortion[],
  typeId: number,
  count: number,
  unitsPerPallet: number,
  totals: LineTotals
) {
  const amounts = portions.map((portion) => ({
    lineId: portion.lineId,
    units: portionUnits(config, portion, totals[portion.lineId] ?? 0)
  }));
  const { config: withNew, uuids } = addPallets(unassignPortions(config, portions), typeId, count);

  const filled = new Map<string, Record<string, number>>(uuids.map((uuid) => [uuid, {}]));
  let cursor = 0;
  let used = 0;
  for (const { lineId, units } of amounts) {
    let remaining = units;
    while (remaining > 0 && cursor < uuids.length) {
      const part = Math.min(remaining, unitsPerPallet - used);
      if (part > 0) {
        const items = filled.get(uuids[cursor])!;
        items[lineId] = (items[lineId] ?? 0) + part;
      }
      used += part;
      remaining -= part;
      if (used >= unitsPerPallet) {
        cursor++;
        used = 0;
      }
    }
  }

  return {
    config: withNew.map((unit) => ({ ...unit, items: filled.get(unit.uuid) ?? unit.items })),
    uuids
  };
}
